import { IMPORTANCES, type Importance } from "@69ly/shared";
import { useTranslation } from "react-i18next";

const BARS: Record<Importance, number> = { generic: 3, useful: 2, situational: 1 };

/** Three bars, like a signal: all lit for generic, two for useful, one for situational. */
export function ImportanceMark({ importance, size = 12 }: { importance: Importance; size?: number }) {
  const lit = BARS[importance];
  return (
    <svg className={`importance-mark importance-${importance}`} width={size} height={size} viewBox="0 0 12 12" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <rect key={i} x={1 + i * 4} y={8 - i * 3} width={2.6} height={3 + i * 3} rx={0.8} className={i < lit ? "is-lit" : undefined} />
      ))}
    </svg>
  );
}

/** "Generic", "Useful", "Situational" with its mark; the meaning in the tooltip. */
export function ImportanceTag({ importance }: { importance: Importance }) {
  const { t } = useTranslation();
  return (
    <span className={`importance-tag importance-${importance}`} title={t(`importance.help.${importance}`)}>
      <ImportanceMark importance={importance} />
      {t(`importance.${importance}`)}
    </span>
  );
}

/** The choice in the stuff and combo forms, with what each level means. */
export function ImportanceField({ value, onChange }: { value: Importance; onChange: (i: Importance) => void }) {
  const { t } = useTranslation();
  return (
    <div className="field importance-field">
      <span id="importance-label">{t("importance.label")}</span>
      <div className="segmented" role="group" aria-labelledby="importance-label">
        {IMPORTANCES.map((i) => (
          <button key={i} type="button" className="seg" aria-pressed={value === i} onClick={() => onChange(i)}>
            <ImportanceMark importance={i} />
            {t(`importance.${i}`)}
          </button>
        ))}
      </div>
      <span className="muted small">{t(`importance.help.${value}`)}</span>
    </div>
  );
}
